/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import IconButton from '@mui/material/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Input from 'components/Input'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { updateQuestion, updateQuestionVariables } from '__generatedTypes__/updateQuestion'
import { GET_ONE_QUESTION, UPDATE_QUESTION } from 'queries/question'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { toast } from 'utils/toast'
import { findOneQuestion, findOneQuestionVariables } from '__generatedTypes__/findOneQuestion'
import * as classes from '../../styles'

export type FormValues = {
  title: string
}

type Props = {
  question: Question
  onSuccessfulEdit: (question: Question) => void
  onStopEditingTitle: (e: React.SyntheticEvent<HTMLButtonElement>) => void
}

export const EditTitleForm: FC<Props> = ({ question, onSuccessfulEdit, onStopEditingTitle }) => {
  const [title, setTitle] = useState(question.title)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updateQuestion] = useMutation<updateQuestion, updateQuestionVariables>(UPDATE_QUESTION)
  const { refetch } = useQuery<findOneQuestion, findOneQuestionVariables>(GET_ONE_QUESTION, {
    variables: {
      params: question.id,
    },
    fetchPolicy: 'network-only',
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      setIsSubmitting(true)
      if (!title) {
        return
      }

      await updateQuestion({
        variables: {
          params: {
            id: question.id,
            title: title,
          },
        },
      })

      await refetch().then(({ data }) => {
        if (data.findOneQuestion) {
          onSuccessfulEdit?.(data.findOneQuestion)
          return
        }
        throw new Error('Failed to refetch question data')
      })

      toast.success('Question title updated successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to update question title')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCheckButtonClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    handleSubmit(e)
  }

  return (
    <div onSubmit={handleSubmit} css={classes.editTitleForm}>
      <Input
        css={classes.titleInput}
        size="small"
        value={title}
        name="title"
        placeholder="Title"
        error={!title}
        fullWidth
        onClick={(e) => e.stopPropagation()}
        onChange={(value) => setTitle(value)}
        disabled={isSubmitting}
      />

      <div css={classes.titleFormActions}>
        <IconButton onClick={handleCheckButtonClick} css={classes.iconButton} disabled={isSubmitting}>
          <CheckIcon />
        </IconButton>
        <IconButton onClick={onStopEditingTitle} css={classes.iconButton}>
          <CloseRoundedIcon css={classes.iconCross} />
        </IconButton>
      </div>
    </div>
  )
}
